import { useState, useEffect, useRef } from "react";
import {
  createMembership,
  getMembershipLookups,
  updateMembership,
} from "../../../api/membershipControlApi";
import { getCsrfToken } from "../../../../../public/api/authApi";
import {
  GlobalButton,
  GlobalSelectField,
  GlobalTextAreaField,
  GlobalTextField,
} from "../../../../../global/forms/GlobalFormFields";
import GlobalFormShell from "../../../../../global/forms/GlobalFormShell";
import {
  normalizeMembershipName,
  sanitizeMembershipNameInput,
  validateMembershipName,
  MEMBERSHIP_NAME_MAX_LEN,
  parseNonNegativeNumber,
  parsePositiveIntCap,
} from "../../../utils/membershipFormUtils";
import { masterSelectOptions } from "../../../utils/masterLookupUtils";

const EMPTY_MEMBERSHIP_FORM = {
  membership_name: "",
  membership_type_id: "",
  duration_type_id: "",
  fee_type_id: "",
  description: "",
  registration_fee: "",
  price: "",
  discount: "",
  benefits: "",
  max_students_allowed: "",
};

function membershipFromListRow(m) {
  return {
    membership_name: m.membership_name || "",
    membership_type_id: m.membership_type_id ?? "",
    duration_type_id: m.duration_type_id ?? "",
    fee_type_id: m.fee_type_id ?? "",
    description: m.description || "",
    registration_fee:
      m.registration_fee != null && m.registration_fee !== ""
        ? Number(m.registration_fee)
        : "",
    price: m.price != null && m.price !== "" ? Number(m.price) : "",
    discount:
      m.discount != null && m.discount !== "" ? Number(m.discount) : "",
    benefits: m.benefits || "",
    max_students_allowed:
      m.max_students_allowed != null && m.max_students_allowed !== ""
        ? Number(m.max_students_allowed)
        : "",
  };
}

function CreateMembershipForm({
  onCancel,
  onSuccess,
  onCancelEdit,
  editingMembership = null,
  lookupsRefreshKey = 0,
}) {
  const isEditMode = Boolean(editingMembership?.id);

  const [formData, setFormData] = useState(EMPTY_MEMBERSHIP_FORM);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [lookups, setLookups] = useState({
    durationTypes: [],
    feeTypes: [],
    membershipTypes: [],
  });
  const alertRef = useRef(null);

  useEffect(() => {
    if (!editingMembership?.id) {
      setFormData({ ...EMPTY_MEMBERSHIP_FORM });
      setError(null);
      setErrorDetails({});
      return;
    }
    setFormData(membershipFromListRow(editingMembership));
    setError(null);
    setErrorDetails({});
  }, [editingMembership]);

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const response = await getMembershipLookups();
        if (response.success && response.data) {
          setLookups({
            durationTypes: response.data.durationTypes || [],
            feeTypes: response.data.feeTypes || [],
            membershipTypes: response.data.membershipTypes || [],
          });
        }
      } catch (err) {
        console.error("Failed to fetch lookups:", err);
      }
    };
    fetchLookups();
  }, [lookupsRefreshKey]);

  const validateAll = () => {
    const errors = {};
    const nameNorm = normalizeMembershipName(formData.membership_name);
    const nameErr = validateMembershipName(nameNorm);
    if (nameErr) errors.membership_name = nameErr;

    if (!formData.membership_type_id) {
      errors.membership_type_id = "Membership type is required";
    }
    if (!formData.duration_type_id) {
      errors.duration_type_id = "Duration model is required";
    }
    if (!formData.fee_type_id) errors.fee_type_id = "Fee category is required";

    const cap =
      formData.max_students_allowed === ""
        ? NaN
        : parseInt(String(formData.max_students_allowed), 10);
    if (Number.isNaN(cap) || cap < 1) {
      errors.max_students_allowed = "Enrollment cap must be at least 1";
    }

    const regFee = parseFloat(formData.registration_fee);
    if (formData.registration_fee === "" || Number.isNaN(regFee) || regFee < 0) {
      errors.registration_fee = "Joining fee is required (0 or greater)";
    }
    const priceNum = parseFloat(formData.price);
    if (formData.price === "" || Number.isNaN(priceNum) || priceNum < 0) {
      errors.price = "Subscription price is required (0 or greater)";
    }
    const discRaw =
      formData.discount === "" ? 0 : parseFloat(formData.discount);
    if (Number.isNaN(discRaw) || discRaw < 0 || discRaw > 100) {
      errors.discount = "Discount must be between 0 and 100 (percent)";
    }

    setErrorDetails(errors);
    return Object.keys(errors).length === 0;
  };

  const setFormField = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errorDetails[name]) {
      setErrorDetails((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleReset = () => {
    setError(null);
    setErrorDetails({});
    if (isEditMode) {
      onCancelEdit?.();
      return;
    }
    setFormData({ ...EMPTY_MEMBERSHIP_FORM });
    if (onCancel) onCancel();
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    setError(null);
    setErrorDetails({});

    if (!validateAll()) {
      setError("Please check the highlighted fields below.");
      setTimeout(() => alertRef.current?.focus(), 0);
      return;
    }

    setLoading(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      const discountNum =
        formData.discount === "" ? 0 : parseFloat(formData.discount);
      const discountCapped = Number.isNaN(discountNum)
        ? 0
        : Math.min(100, Math.max(0, discountNum));
      const payload = {
        ...formData,
        membership_name: normalizeMembershipName(formData.membership_name),
        registration_fee: parseFloat(formData.registration_fee),
        price: parseFloat(formData.price),
        discount: discountCapped,
        max_students_allowed: parseInt(String(formData.max_students_allowed), 10),
        membership_type_id: Number(formData.membership_type_id),
        duration_type_id: Number(formData.duration_type_id),
        fee_type_id: Number(formData.fee_type_id),
      };

      if (isEditMode) {
        await updateMembership(editingMembership.id, payload, csrfToken);
        onSuccess?.("Membership updated successfully.");
      } else {
        const result = await createMembership(payload, csrfToken);

        if (result.error) {
          setError(
            typeof result.error.message === "string"
              ? result.error.message
              : "Failed to create membership",
          );
          setErrorDetails(
            result.error.details && typeof result.error.details === "object"
              ? result.error.details
              : {},
          );
          setTimeout(() => alertRef.current?.focus(), 0);
          return;
        }

        onSuccess?.("Membership created successfully!");
        setFormData({ ...EMPTY_MEMBERSHIP_FORM });
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred");
      setTimeout(() => alertRef.current?.focus(), 0);
    } finally {
      setLoading(false);
    }
  };

  const ALERT_ID = "create-membership-alert";

  return (
    <GlobalFormShell
      legend={isEditMode ? "Edit membership plan" : "Create membership plan"}
      showAlert={!!error}
      alertText={error}
      alertId={ALERT_ID}
      formAlertRef={alertRef}
      actions={
        <>
          {isEditMode ? (
            <GlobalButton
              type="button"
              variant="ghost"
              disabled={loading}
              onClick={handleReset}
            >
              Cancel edit
            </GlobalButton>
          ) : null}
          <GlobalButton
            type="submit"
            variant="primary"
            disabled={loading}
            onClick={handleSubmit}
            loading={loading}
          >
            {isEditMode ? "Save changes" : "Create membership"}
          </GlobalButton>
          {!isEditMode ? (
            <GlobalButton
              type="button"
              variant="ghost"
              onClick={handleReset}
              disabled={loading}
            >
              Clear form
            </GlobalButton>
          ) : null}
        </>
      }
    >
      <div className="gff-field--full" style={{ marginBottom: "-0.5rem" }}>
        <h4
          style={{
            margin: "0.5rem 0",
            color: "var(--ub-teal-dark)",
            fontSize: "0.85rem",
            fontWeight: 700,
          }}
        >
          Core Configuration
        </h4>
      </div>

      <GlobalTextField
        id="membership_name"
        label="Membership Name"
        value={formData.membership_name}
        onChange={(v) =>
          setFormField("membership_name", sanitizeMembershipNameInput(v))
        }
        onBlur={() =>
          setFormData((prev) => ({
            ...prev,
            membership_name: String(prev.membership_name).trim(),
          }))
        }
        error={errorDetails.membership_name}
        baseAlertId={ALERT_ID}
        maxLength={MEMBERSHIP_NAME_MAX_LEN}
        required
      />

      <GlobalSelectField
        id="membership_type_id"
        label="Category"
        value={formData.membership_type_id}
        onChange={(v) => setFormField("membership_type_id", v ? Number(v) : "")}
        options={masterSelectOptions(lookups.membershipTypes, {
          labelKey: "type_name",
          selectedValue: formData.membership_type_id,
        })}
        error={errorDetails.membership_type_id}
        baseAlertId={ALERT_ID}
        required
      />

      <GlobalTextAreaField
        classNames={{ fieldWrap: "gff-field gff-field--full" }}
        id="description"
        label="Internal Description"
        value={formData.description}
        onChange={(v) => setFormField("description", v)}
        error={errorDetails.description}
        baseAlertId={ALERT_ID}
      />

      <GlobalTextAreaField
        classNames={{ fieldWrap: "gff-field gff-field--full" }}
        id="benefits"
        label="Subscriber Benefits"
        value={formData.benefits}
        onChange={(v) => setFormField("benefits", v)}
        error={errorDetails.benefits}
        baseAlertId={ALERT_ID}
      />

      <div
        className="gff-field--full"
        style={{ marginBottom: "-0.5rem", marginTop: "0.5rem" }}
      >
        <h4
          style={{
            margin: "0.5rem 0",
            color: "var(--ub-teal-dark)",
            fontSize: "0.85rem",
            fontWeight: 700,
          }}
        >
          Schedule & Capacity
        </h4>
      </div>

      <GlobalSelectField
        id="duration_type_id"
        label="Duration Model"
        value={formData.duration_type_id}
        onChange={(v) => setFormField("duration_type_id", v ? Number(v) : "")}
        options={masterSelectOptions(lookups.durationTypes, {
          labelKey: "duration_name",
          selectedValue: formData.duration_type_id,
        })}
        error={errorDetails.duration_type_id}
        baseAlertId={ALERT_ID}
        required
      />

      <GlobalTextField
        id="max_students_allowed"
        label="Enrollment Cap"
        type="number"
        min={0}
        step={1}
        inputMode="numeric"
        value={
          formData.max_students_allowed === ""
            ? ""
            : formData.max_students_allowed
        }
        onChange={(v) =>
          setFormField("max_students_allowed", parsePositiveIntCap(v))
        }
        error={errorDetails.max_students_allowed}
        baseAlertId={ALERT_ID}
        required
      />

      <div
        className="gff-field--full"
        style={{ marginBottom: "-0.5rem", marginTop: "0.5rem" }}
      >
        <h4
          style={{
            margin: "0.5rem 0",
            color: "var(--ub-teal-dark)",
            fontSize: "0.85rem",
            fontWeight: 700,
          }}
        >
          Financial Structure
        </h4>
      </div>

      <GlobalTextField
        id="registration_fee"
        label="Joining Fee"
        type="number"
        min={0}
        step="0.01"
        value={
          formData.registration_fee === "" ? "" : formData.registration_fee
        }
        onChange={(v) =>
          setFormField("registration_fee", parseNonNegativeNumber(v))
        }
        error={errorDetails.registration_fee}
        baseAlertId={ALERT_ID}
        required
      />

      <GlobalTextField
        id="price"
        label="Subscription Price"
        type="number"
        min={0}
        step="0.01"
        value={formData.price === "" ? "" : formData.price}
        onChange={(v) => setFormField("price", parseNonNegativeNumber(v))}
        error={errorDetails.price}
        baseAlertId={ALERT_ID}
        required
      />

      <GlobalTextField
        id="discount"
        label="Standard Discount (%)"
        type="number"
        min={0}
        max={100}
        step="0.01"
        value={formData.discount === "" ? "" : formData.discount}
        onChange={(v) => {
          if (v === "") return setFormField("discount", "");
          const n = parseNonNegativeNumber(v);
          if (n === "") return setFormField("discount", "");
          setFormField("discount", Math.min(100, n));
        }}
        error={errorDetails.discount}
        baseAlertId={ALERT_ID}
      />

      <GlobalSelectField
        id="fee_type_id"
        label="Fee Category"
        value={formData.fee_type_id}
        onChange={(v) => setFormField("fee_type_id", v ? Number(v) : "")}
        options={masterSelectOptions(lookups.feeTypes, {
          labelKey: "fee_type_name",
          selectedValue: formData.fee_type_id,
        })}
        error={errorDetails.fee_type_id}
        baseAlertId={ALERT_ID}
        required
      />
    </GlobalFormShell>
  );
}

export default function MembershipCreateSection({
  lookupsRefreshKey,
  editingMembership,
  onCancelEdit,
  onSuccess,
  canCreate = false,
  canEdit = false,
}) {
  const isEditMode = Boolean(editingMembership?.id);
  if (!canCreate && !(canEdit && isEditMode)) {
    return null;
  }

  return (
    <div
      id="ms-create"
      className="user-builder-panel"
      style={{ scrollMarginTop: "2rem" }}
    >
      <div className="user-builder-panel__head">
        <h3 className="user-builder-panel__title">Plan architect</h3>
        <p className="user-builder-panel__hint">
          Create a new membership or choose <b>Edit</b> in the directory below
          to update an existing plan here.
        </p>
      </div>
      <CreateMembershipForm
        lookupsRefreshKey={lookupsRefreshKey}
        editingMembership={editingMembership}
        onCancelEdit={onCancelEdit}
        onCancel={() => {}}
        onSuccess={onSuccess}
      />
    </div>
  );
}
